import { useState, useEffect, useMemo } from "react";
import { useFormik } from "formik";
import { UpdateUserService, GetUserProfileService } from "shared/services/user";
import { toastMessage } from "shared/components/common/toast";
import { handleErrors } from "shared/utils/helper";
import { useSelector, useDispatch } from "react-redux";
import { setAuthReducer } from "shared/redux/reducers/authSlice";
import { designerAccountValidation } from "shared/utils/validation";
import { Icons } from "assets";
import classNames from "classnames";
import styles from "./styles.module.scss";
import ProfileUpload from "shared/components/common/customProfileUpload";
import CustomPageHeading from "shared/components/common/customPageHeading";
import CustomInput from "shared/components/common/customInput";
import CustomCard from "shared/components/common/customCardComponent";
import NotificationPreference from "shared/components/common/customNotificationPreference";
import CustomButton from "shared/components/common/customButton";
import CardSkeleton from "shared/components/common/cardSkeleton";
import MobileLogoutButton from "shared/components/dashboardCommon/logoutButton";
import ChangePasswordSection from "shared/components/common/changePassword";

const DesignerAccountSetting = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state: any) => state.root.auth);
  const user = useMemo(() => auth?.user || {}, [auth?.user]);

  const [profileFile, setProfileFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [deleteProfileAsset, setDeleteProfileAsset] = useState<boolean>(false);
  const [profileData, setProfileData] = useState<any>(null);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [formData, setFormData] = useState({
    displayName: user.display_name || "",
    receiveEmailNotification: user.receive_email_notification || false,
  });

  useEffect(() => {
    fetchUserProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (user) {
      setFormData({
        displayName: user.display_name || "",
        receiveEmailNotification: user.receive_email_notification || false,
      });
      const profileImageUrl =
        user.profile_asset?.path || user.path || user.user_profile_asset?.path;
      if (profileImageUrl) {
        setPreview(profileImageUrl);
      }
    }
  }, [user]);

  const fetchUserProfile = async () => {
    try {
      setLoadingProfile(true);
      const response = await GetUserProfileService();

      if (response.status === 200) {
        const data = response.data?.data || response.data;
        setProfileData(data);

        const updatedUser = {
          ...user,
          display_name: data.display_name,
          email: data.email,
          user_type: data.user_type,
          phone: data.phone,
          first_name: data.first_name,
          last_name: data.last_name,
          receive_email_notification: data.receive_email_notifications,
          created_at: data.created_at,
        };

        if (data.profile_image_url) {
          updatedUser.profile_asset = { path: data.profile_image_url };
          updatedUser.path = data.profile_image_url;
        }

        dispatch(setAuthReducer({ user: updatedUser }));
      }
    } catch (error: any) {
      handleErrors(error);
    } finally {
      setLoadingProfile(false);
    }
  };

  const formatMemberSince = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
    });
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      displayName: formData.displayName,
      receiveEmailNotification: formData.receiveEmailNotification,
    },
    validationSchema: designerAccountValidation,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        setSubmitting(true);

        let payload: any;
        if (profileFile || deleteProfileAsset) {
          payload = new FormData();
          payload.append("display_name", values.displayName);
          payload.append(
            "receive_email_notification",
            values.receiveEmailNotification.toString(),
          );

          if (profileFile) {
            payload.append("profile_asset", profileFile);
          }

          if (deleteProfileAsset) {
            payload.append("delete_profile_asset", "true");
          }
        } else {
          payload = {
            display_name: values.displayName,
            receive_email_notification: values.receiveEmailNotification,
          };
        }

        const response = await UpdateUserService(payload);

        if (response.status === 200) {
          const message =
            response.data?.message ||
            response.data?.data?.message ||
            "Profile updated successfully";
          toastMessage("success", message);
          setFormData(values);

          const hadProfileFile = !!profileFile;
          const hadDeleteRequest = deleteProfileAsset;

          const updatedUser = {
            ...user,
            display_name: values.displayName,
            receive_email_notification: values.receiveEmailNotification,
          };

          if (hadDeleteRequest) {
            updatedUser.profile_asset = null;
            updatedUser.path = null;
            setPreview(null);
          } else if (hadProfileFile) {
            const newProfileAssetPath =
              response.data?.profile_asset ||
              response.data?.profile_asset?.path ||
              response.data?.path ||
              response.data?.data?.profile_asset ||
              response.data?.data?.path;

            if (newProfileAssetPath) {
              updatedUser.profile_asset = {
                ...user.profile_asset,
                path: newProfileAssetPath,
              };
              updatedUser.path = newProfileAssetPath;
              setPreview(newProfileAssetPath);
            }
          }

          dispatch(setAuthReducer({ user: updatedUser }));

          setProfileFile(null);
          setDeleteProfileAsset(false);
        }
      } catch (error: any) {
        handleErrors(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    isValid,
    isSubmitting: formikSubmitting,
  } = formik;

  const handleRemoveProfilePicture = () => {
    setProfileFile(null);
    setPreview(null);
    setDeleteProfileAsset(true);
  };

  return (
    <div
      className={classNames(
        styles.pageWrapper,
        "flex flex-col md:gap-5 sm:gap-4 gap-3 w-full h-full",
      )}
    >
      <div className="sm:pr-0 pr-12">
        <CustomPageHeading
          title="Profile & Settings"
          description="Tailor your creative setup"
        />
      </div>

      <div className={classNames(styles.contentEnclosure, "grid")}>
        <ProfileUpload
          title={user.display_name || ""}
          description={
            user.user_type
              ? user.user_type
                  .split("_")
                  .map(
                    (word: string) =>
                      word.charAt(0).toUpperCase() +
                      word.slice(1).toLowerCase(),
                  )
                  .join(" ")
              : ""
          }
          defaultPreview={preview}
          onChange={(file, url) => {
            setProfileFile(file);
            setPreview(url);
            setDeleteProfileAsset(false);
          }}
          onRemove={handleRemoveProfilePicture}
        />

        <form onSubmit={handleSubmit}>
          <div className={classNames(styles.alignmentEnclosure)}>
            <div className={classNames(styles.inputEnclosure, "flex mt-4")}>
              <CustomInput
                label="Display Name"
                required
                placeholder="Sara Chen"
                name="displayName"
                value={values.displayName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  handleChange(e);
                }}
                onBlur={handleBlur}
                error={
                  touched.displayName && errors.displayName
                    ? errors.displayName
                    : ""
                }
                customInputContainer={styles.inputContainer}
                notRequiredMargin={true}
              />

              <CustomInput
                label="Email"
                placeholder="Sarahchendesign@gmail.com"
                name="email"
                value={user.email || ""}
                readOnly
                customInputContainer={styles.inputContainer}
                notRequiredMargin={true}
              />
            </div>

            <div className={classNames(styles.cardEnclosure)}>
              {loadingProfile ? (
                <>
                  <CardSkeleton
                    count={1}
                    title="Member Since"
                    icon={<Icons.Profile />}
                  />
                  <CardSkeleton
                    count={1}
                    title="Projects Completed"
                    icon={<Icons.ApprovedState />}
                  />
                  <CardSkeleton
                    count={1}
                    title="Active Projects"
                    icon={<Icons.BriefCase />}
                  />
                </>
              ) : (
                <>
                  <CustomCard
                    title="Member Since"
                    description={formatMemberSince(
                      profileData?.created_at || user.created_at,
                    )}
                    icon={<Icons.Profile />}
                  />
                  <CustomCard
                    title={String(profileData?.total_completed_projects || 0)}
                    description="Projects Completed"
                    icon={<Icons.ApprovedState />}
                  />
                  <CustomCard
                    title={String(profileData?.total_active_projects || 0)}
                    description="Active Projects"
                    icon={<Icons.BriefCase />}
                  />
                </>
              )}
            </div>

            <div className={classNames(styles.buttonEnclosure, "pt-2")}>
              <CustomButton
                title="Save Changes"
                containerStyle={classNames(styles.submitButton)}
                type="submit"
                loading={formikSubmitting}
                disabled={formikSubmitting || !isValid}
              />
            </div>
          </div>
        </form>
      </div>

      <ChangePasswordSection />

      <NotificationPreference
        icon={<Icons.EditState />}
        emailEnabled={values.receiveEmailNotification}
        onEmailToggleChange={(checked: boolean) => {
          formik.setFieldValue("receiveEmailNotification", checked);
        }}
      />

      <MobileLogoutButton />
    </div>
  );
};

export default DesignerAccountSetting;
